import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { CompactTable } from '../../components/ui/CompactTable';
import { Plus, Trash2, Pencil } from 'lucide-react';
import { api } from '../../services/api';
import { toast } from 'sonner';
import { TableFilterBar } from '../../components/table-filters/TableFilterBar';
import { useTableFilterState } from '../../hooks/useTableFilterState';
import { FieldErrorText } from '../../components/forms/FieldErrorText';
import { showValidationSummaryToast } from '../../components/forms/ValidationSummaryToast';
import { focusFirstInvalidField, validateRequiredFields } from '../../components/forms/formValidationUtils';

const formatNumber = (value: number, decimals = 2) =>
    new Intl.NumberFormat('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(value || 0);

const toNumber = (value: string | number, fallback = 0) => {
    const num = typeof value === 'number' ? value : parseFloat(value);
    return Number.isFinite(num) ? num : fallback;
};

type OverheadCostManagerProps = {
    embedded?: boolean;
};

export function OverheadCostManager({ embedded = false }: OverheadCostManagerProps) {
    const [tab, setTab] = useState<'main' | 'sub'>('main');
    const [summary, setSummary] = useState<any>(null);
    const [mainCategories, setMainCategories] = useState<any[]>([]);
    const [subCategories, setSubCategories] = useState<any[]>([]);
    const [units, setUnits] = useState<any[]>([]);
    const [selectedMainCategoryId, setSelectedMainCategoryId] = useState<number | undefined>();
    const [loading, setLoading] = useState(false);
    const [editingMainId, setEditingMainId] = useState<number | null>(null);
    const [editingSubId, setEditingSubId] = useState<number | null>(null);
    const [isMainModalOpen, setIsMainModalOpen] = useState(false);
    const [isSubModalOpen, setIsSubModalOpen] = useState(false);
    const [confirmDialog, setConfirmDialog] = useState<{
        open: boolean;
        type: 'main' | 'sub' | null;
        id?: number | null;
    }>({ open: false, type: null, id: null });
    const { state, setFilter, isDirty } = useTableFilterState({ q_main: '', q_sub: '' });
    const [mainErrors, setMainErrors] = useState<Record<string, string>>({});
    const [subErrors, setSubErrors] = useState<Record<string, string>>({});
    
    const [mainForm, setMainForm] = useState({ main_category: '', amortization_percentage: 0, months: 0 });
    const [subForm, setSubForm] = useState({
        oh_main_category_id: 0,
        sub_category: '',
        unit_id: 0,
        quantity: 0,
        cost: 0,
        cost_percentage: 100,
    });

    const computedSubTotal = useMemo(() => {
        return toNumber(subForm.quantity) * toNumber(subForm.cost) * (toNumber(subForm.cost_percentage) / 100);
    }, [subForm]);

    const summaryById = useMemo(() => {
        const rows = summary?.rows || [];
        return new Map(rows.map((row: any) => [row.oh_main_category_id, row]));
    }, [summary]);
    const filteredMainCategories = useMemo(() => {
        const query = state.q_main.trim().toLowerCase();
        if (!query) return mainCategories;
        return mainCategories.filter((cat: any) =>
            String(cat.main_category || '').toLowerCase().includes(query) ||
            String(cat.oh_main_category_id || '').includes(query)
        );
    }, [mainCategories, state.q_main]);
    const filteredSubCategories = useMemo(() => {
        const query = state.q_sub.trim().toLowerCase();
        if (!query) return subCategories;
        return subCategories.filter((sub: any) =>
            String(sub.main_category || sub.main_category_name || '').toLowerCase().includes(query) ||
            String(sub.sub_category || '').toLowerCase().includes(query) ||
            String(sub.unit || sub.unit_name || '').toLowerCase().includes(query)
        );
    }, [subCategories, state.q_sub]);

    // Column definitions for main categories table
    const mainColumns = [
        { header: 'Category', accessor: (cat: any) => cat.main_category, className: 'text-left' },
        { header: 'Amort %', accessor: (cat: any) => formatNumber(cat.amortization_percentage, 0), className: 'text-center' },
        { header: 'Months', accessor: (cat: any) => cat.months, className: 'text-center' },
        { header: 'Cost', accessor: (cat: any) => {
            const summaryRow = (summaryById.get(cat.oh_main_category_id) as any) || null;
            return formatNumber(summaryRow?.total_by_main || 0);
        }, className: 'text-right' },
        { header: 'Amort.', accessor: (cat: any) => {
            const summaryRow = (summaryById.get(cat.oh_main_category_id) as any) || null;
            return formatNumber(summaryRow?.amortization_extra || 0);
        }, className: 'text-right' },
        { header: 'SQM', accessor: (cat: any) => {
            const summaryRow = (summaryById.get(cat.oh_main_category_id) as any) || null;
            return formatNumber(summaryRow?.sqm_rate || 0);
        }, className: 'text-right' },
        { header: 'Rate / SQM', accessor: (cat: any) => {
            const summaryRow = (summaryById.get(cat.oh_main_category_id) as any) || null;
            return formatNumber(summaryRow?.rate_per_sqm || 0);
        }, className: 'text-right' },
        { header: 'Actions', accessor: (cat: any) => (
            <div className="flex gap-1 justify-center">
                <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => {
                        handleEditMainCategory(cat);
                        setIsMainModalOpen(true);
                    }}
                >
                    <Pencil className="w-4 h-4 text-blue-600" />
                </Button>
                <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => handleDeleteMainCategory(cat.oh_main_category_id)}
                >
                    <Trash2 className="w-4 h-4 text-red-600" />
                </Button>
            </div>
        ), className: 'text-center' }
    ];

    // Column definitions for sub categories table
    const subColumns = [
        { header: 'Main Category', accessor: (sub: any) => sub.main_category ?? sub.main_category_name, className: 'text-left' },
        { header: 'Sub Category', accessor: (sub: any) => sub.sub_category, className: 'text-left' },
        { header: 'Unit', accessor: (sub: any) => sub.unit ?? sub.unit_name, className: 'text-left' },
        { header: 'Qty', accessor: (sub: any) => formatNumber(sub.quantity, 0), className: 'text-right' },
        { header: 'Cost', accessor: (sub: any) => `AED ${formatNumber(sub.cost)}`, className: 'text-right' },
        { header: '%', accessor: (sub: any) => formatNumber(sub.cost_percentage, 0), className: 'text-right' },
        { header: 'Sub Total', accessor: (sub: any) => `AED ${formatNumber(sub.sub_total)}`, className: 'text-right' },
        { header: 'Actions', accessor: (sub: any) => (
            <div className="flex gap-1 justify-center">
                <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => {
                        handleEditSubCategory(sub);
                        setIsSubModalOpen(true);
                    }}
                >
                    <Pencil className="w-4 h-4 text-blue-600" />
                </Button>
                <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => handleDeleteSubCategory(sub.oh_sub_category_id)}
                >
                    <Trash2 className="w-4 h-4 text-red-600" />
                </Button>
            </div>
        ), className: 'text-center' }
    ];

    // Load Data
    const loadMainCategories = async () => {
        const data = await api.overhead.listMainCategories();
        setMainCategories(data || []);
    };

    const loadUnits = async () => {
        const data = await api.masterData.list('tbl_units');
        setUnits(data || []);
    };

    const loadSubCategories = async () => {
        const data = await api.overhead.listSubCategories(selectedMainCategoryId);
        setSubCategories(data || []);
    };

    const loadSummary = async () => {
        const data = await api.overhead.getSummary();
        setSummary(data);
    };

    useEffect(() => {
        loadMainCategories();
        loadUnits();
        loadSummary();
    }, []);

    useEffect(() => {
        loadSubCategories();
        clearSubForm();
    }, [selectedMainCategoryId]);

    // Main Category Handlers
    const handleSaveMainCategory = async () => {
        const validation = validateRequiredFields([
            { key: 'main_category', label: 'Category Name', value: mainForm.main_category, required: true },
        ]);
        if (!validation.valid) {
            setMainErrors(validation.errorsByField);
            showValidationSummaryToast();
            focusFirstInvalidField(validation.firstInvalidField);
            return;
        }
        setMainErrors({});

        setLoading(true);
        try {
            const payload = {
                main_category: mainForm.main_category,
                amortization_percentage: toNumber(mainForm.amortization_percentage),
                months: toNumber(mainForm.months),
            };

            if (editingMainId) {
                await api.masterData.update('tbl_oh_main_categories', editingMainId, payload);
                toast.success('Category updated');
            } else {
                await api.masterData.create('tbl_oh_main_categories', payload);
                toast.success('Category created');
            }
            clearMainForm();
            setIsMainModalOpen(false);
            loadMainCategories();
            loadSummary();
        } catch (error) {
            toast.error('Failed to save: ' + (error as any).message);
        } finally {
            setLoading(false);
        }
    };

    const handleEditMainCategory = (record: any) => {
        setEditingMainId(record.oh_main_category_id);
        setMainForm({
            main_category: record.main_category,
            amortization_percentage: record.amortization_percentage,
            months: record.months,
        });
    };

    const handleDeleteMainCategory = async (id: number) => {
        setConfirmDialog({ open: true, type: 'main', id });
    };

    const clearMainForm = () => {
        setEditingMainId(null);
        setMainForm({ main_category: '', amortization_percentage: 0, months: 0 });
        setMainErrors({});
    };

    // Sub Category Handlers
    const handleSaveSubCategory = async () => {
        const validation = validateRequiredFields([
            { key: 'oh_main_category_id', label: 'Main Category', value: subForm.oh_main_category_id, required: true },
            { key: 'sub_category', label: 'Sub Category', value: subForm.sub_category, required: true },
            { key: 'unit_id', label: 'Unit', value: subForm.unit_id, required: true },
        ]);
        if (!validation.valid) {
            setSubErrors(validation.errorsByField);
            showValidationSummaryToast();
            focusFirstInvalidField(validation.firstInvalidField);
            return;
        }
        setSubErrors({});

        setLoading(true);
        try {
            const payload = {
                oh_main_category_id: toNumber(subForm.oh_main_category_id),
                sub_category: subForm.sub_category,
                unit_id: toNumber(subForm.unit_id),
                quantity: toNumber(subForm.quantity),
                cost: toNumber(subForm.cost),
                cost_percentage: toNumber(subForm.cost_percentage),
            };

            if (editingSubId) {
                await api.overhead.updateSubCategory(editingSubId, payload);
                toast.success('Sub category updated');
            } else {
                await api.overhead.createSubCategory(payload);
                toast.success('Sub category created');
            }
            clearSubForm();
            setIsSubModalOpen(false);
            loadSubCategories();
            loadSummary();
        } catch (error) {
            toast.error('Failed to save: ' + (error as any).message);
        } finally {
            setLoading(false);
        }
    };

    const handleEditSubCategory = (record: any) => {
        setEditingSubId(record.oh_sub_category_id);
        setSubForm({
            oh_main_category_id: record.oh_main_category_id,
            sub_category: record.sub_category,
            unit_id: record.unit_id,
            quantity: record.quantity,
            cost: record.cost,
            cost_percentage: record.cost_percentage,
        });
        setSelectedMainCategoryId(record.oh_main_category_id);
    };

    const handleDeleteSubCategory = async (id: number) => {
        setConfirmDialog({ open: true, type: 'sub', id });
    };

    const performConfirmedDelete = async () => {
        const { type, id } = confirmDialog;
        if (!id || !type) {
            setConfirmDialog({ open: false, type: null, id: null });
            return;
        }
        try {
            if (type === 'main') {
                await api.masterData.delete('tbl_oh_main_categories', id);
                toast.success('Category deleted');
                loadMainCategories();
                loadSummary();
            } else {
                await api.overhead.deleteSubCategory(id);
                toast.success('Sub category deleted');
                loadSubCategories();
                loadSummary();
            }
        } catch (error) {
            toast.error('Failed to delete');
        } finally {
            setConfirmDialog({ open: false, type: null, id: null });
        }
    };

    const clearSubForm = () => {
        const fallbackMain = selectedMainCategoryId ?? mainCategories[0]?.oh_main_category_id ?? 0;
        setEditingSubId(null);
        setSubErrors({});
        setSubForm({
            oh_main_category_id: fallbackMain,
            sub_category: '',
            unit_id: 0,
            quantity: 0,
            cost: 0,
            cost_percentage: 100,
        });
    };

    // Render Main Categories Tab
    const renderMainTab = () => (
        <div className="p-4 space-y-4">
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Main Categories</h3>
                <Button
                    onClick={() => {
                        clearMainForm();
                        setIsMainModalOpen(true);
                    }}
                >
                    <Plus className="w-4 h-4 mr-2" /> Add Category
                </Button>
            </div>

            <div className="overflow-x-auto">
                <TableFilterBar
                    searchValue={state.q_main}
                    onSearchChange={(value) => setFilter('q_main', value)}
                    searchPlaceholder="Search main categories..."
                    isDirty={isDirty && !!state.q_main}
                    onClear={() => setFilter('q_main', '')}
                    rightSlot={`${filteredMainCategories.length} results`}
                />
                <CompactTable
                    data={filteredMainCategories}
                    columns={mainColumns}
                    keyField="oh_main_category_id"
                    loading={loading}
                    emptyMessage="No categories found"
                />
                {/* Footer with totals as a Card */}
                <div className="mt-6 border-t pt-4">
                    <Card>
                        <CardContent className="p-4">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                <div className="text-slate-600">
                                    <span className="font-medium">Total OH / SQM</span>
                                    <div className="text-xs text-slate-500">(Total overhead allocated per square meter)</div>
                                </div>
                                <div className="flex items-baseline gap-3">
                                    <span className="text-2xl font-bold text-slate-800">{formatNumber(summary?.total_rate_per_sqm || 0)}</span>
                                    <span className="text-sm text-slate-500">AED / sqm</span>
                                </div>
                            </div>

                            <div className="mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                <div className="text-slate-600">
                                    <span className="font-medium">OH / KG</span>
                                    <div className="text-xs text-slate-500">(Overhead per kilogram of material)</div>
                                </div>
                                <div className="flex items-baseline gap-3">
                                    <span className="text-xl font-semibold text-slate-800">{formatNumber(summary?.oh_per_kg || 0)}</span>
                                    <span className="text-sm text-slate-500">AED / kg</span>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );

    // Render Sub Categories Tab
    const renderSubTab = () => (
        <div className="p-4 space-y-4">
            <div>
                <label className="block text-sm font-medium mb-2">Filter by Main Category</label>
                <select
                    className="border rounded-md px-3 py-2 text-sm w-full max-w-xs"
                    value={selectedMainCategoryId || ''}
                    onChange={(e) => setSelectedMainCategoryId(e.target.value ? toNumber(e.target.value) : undefined)}
                >
                    <option value="">All Categories</option>
                    {mainCategories.map((cat) => (
                        <option key={cat.oh_main_category_id} value={cat.oh_main_category_id}>
                            {cat.main_category}
                        </option>
                    ))}
                </select>
            </div>

            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold">Sub Category List</h3>
                    <Button
                        onClick={() => {
                            clearSubForm();
                            const fallbackMain = selectedMainCategoryId ?? mainCategories[0]?.oh_main_category_id ?? 0;
                            setSubForm((prev) => ({ ...prev, oh_main_category_id: fallbackMain }));
                            setIsSubModalOpen(true);
                        }}
                    >
                        <Plus className="w-4 h-4 mr-2" /> Add Sub Category
                    </Button>
                </div>

                <div className="overflow-x-auto">
                    <TableFilterBar
                        searchValue={state.q_sub}
                        onSearchChange={(value) => setFilter('q_sub', value)}
                        searchPlaceholder="Search sub categories..."
                        isDirty={isDirty && !!state.q_sub}
                        onClear={() => setFilter('q_sub', '')}
                        rightSlot={`${filteredSubCategories.length} results`}
                    />
                    <CompactTable
                        data={filteredSubCategories}
                        columns={subColumns}
                        keyField="oh_sub_category_id"
                        loading={loading}
                        emptyMessage="No sub categories found"
                    />
                </div>
            </div>
        </div>
    );

    return (
        <div className={embedded ? 'flex min-h-[72vh] flex-col bg-white' : 'h-full flex flex-col'}>
            {/* Tab Navigation */}
            <div className="flex gap-2 border-b bg-slate-50/50 px-4 py-2">
                <button
                    onClick={() => setTab('main')}
                    className={`px-4 py-2 font-medium transition-colors ${
                        tab === 'main'
                            ? 'border-b-2 border-blue-600 text-blue-600'
                            : 'text-gray-600 hover:text-gray-900'
                    }`}
                >
                    Main Categories
                </button>
                <button
                    onClick={() => setTab('sub')}
                    className={`px-4 py-2 font-medium transition-colors ${
                        tab === 'sub'
                            ? 'border-b-2 border-blue-600 text-blue-600'
                            : 'text-gray-600 hover:text-gray-900'
                    }`}
                >
                    Sub Categories
                </button>
            </div>

            {/* Tab Content */}
            <div className="flex-1 overflow-auto">
                {tab === 'main' && renderMainTab()}
                {tab === 'sub' && renderSubTab()}
            </div>

            <Dialog open={isMainModalOpen} onOpenChange={setIsMainModalOpen}>
                <DialogContent className="max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{editingMainId ? 'Edit Main Category' : 'Add Main Category'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label>Category Name *</Label>
                            <Input
                                id="main_category"
                                name="main_category"
                                aria-invalid={!!mainErrors.main_category}
                                aria-required="true"
                                value={mainForm.main_category}
                                onChange={(e) => setMainForm({ ...mainForm, main_category: e.target.value })}
                                placeholder="e.g., FACTORY SHED"
                            />
                            <FieldErrorText message={mainErrors.main_category} />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Amortization %</Label>
                                <Input
                                    type="number"
                                    value={mainForm.amortization_percentage}
                                    onChange={(e) => setMainForm({ ...mainForm, amortization_percentage: toNumber(e.target.value) })}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Months</Label>
                                <Input
                                    type="number"
                                    value={mainForm.months}
                                    onChange={(e) => setMainForm({ ...mainForm, months: toNumber(e.target.value) })}
                                />
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setIsMainModalOpen(false); clearMainForm(); }}>
                            Cancel
                        </Button>
                        <Button onClick={handleSaveMainCategory} disabled={loading}>
                            {editingMainId ? 'Update' : 'Add'} Category
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={isSubModalOpen} onOpenChange={setIsSubModalOpen}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{editingSubId ? 'Edit Sub Category' : 'Add Sub Category'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Main Category *</Label>
                                <select
                                    id="oh_main_category_id"
                                    name="oh_main_category_id"
                                    aria-invalid={!!subErrors.oh_main_category_id}
                                    aria-required="true"
                                    className="border rounded-md px-3 py-2 text-sm w-full"
                                    value={subForm.oh_main_category_id}
                                    onChange={(e) => setSubForm({ ...subForm, oh_main_category_id: toNumber(e.target.value) })}
                                >
                                    <option value="">Select</option>
                                    {mainCategories.map((cat) => (
                                        <option key={cat.oh_main_category_id} value={cat.oh_main_category_id}>
                                            {cat.main_category}
                                        </option>
                                    ))}
                                </select>
                                <FieldErrorText message={subErrors.oh_main_category_id} />
                            </div>
                            <div className="space-y-2">
                                <Label>Sub Category *</Label>
                                <Input
                                    id="sub_category"
                                    name="sub_category"
                                    aria-invalid={!!subErrors.sub_category}
                                    aria-required="true"
                                    value={subForm.sub_category}
                                    onChange={(e) => setSubForm({ ...subForm, sub_category: e.target.value })}
                                    placeholder="e.g., Mixers"
                                />
                                <FieldErrorText message={subErrors.sub_category} />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <div className="space-y-2">
                                <Label>Unit *</Label>
                                <select
                                    id="unit_id"
                                    name="unit_id"
                                    aria-invalid={!!subErrors.unit_id}
                                    aria-required="true"
                                    className="border rounded-md px-3 py-2 text-sm w-full"
                                    value={subForm.unit_id}
                                    onChange={(e) => setSubForm({ ...subForm, unit_id: toNumber(e.target.value) })}
                                >
                                    <option value="">Select</option>
                                    {units.map((u: any) => (
                                        <option key={u.unit_id} value={u.unit_id}>
                                            {u.unit_name ?? u.unit}
                                        </option>
                                    ))}
                                </select>
                                <FieldErrorText message={subErrors.unit_id} />
                            </div>
                            <div className="space-y-2">
                                <Label>Quantity</Label>
                                <Input
                                    type="number"
                                    value={subForm.quantity}
                                    onChange={(e) => setSubForm({ ...subForm, quantity: toNumber(e.target.value) })}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Cost (AED)</Label>
                                <Input
                                    type="number"
                                    value={subForm.cost}
                                    onChange={(e) => setSubForm({ ...subForm, cost: toNumber(e.target.value) })}
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <div className="space-y-2">
                                <Label>% of Category</Label>
                                <Input
                                    type="number"
                                    value={subForm.cost_percentage}
                                    onChange={(e) => setSubForm({ ...subForm, cost_percentage: toNumber(e.target.value) })}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Sub Total</Label>
                                <Input value={formatNumber(computedSubTotal)} disabled className="bg-slate-50" />
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setIsSubModalOpen(false); clearSubForm(); }}>
                            Cancel
                        </Button>
                        <Button onClick={handleSaveSubCategory} disabled={loading}>
                            {editingSubId ? 'Update' : 'Add'} Sub Category
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={confirmDialog.open} onOpenChange={(open) => setConfirmDialog((s) => ({ ...s, open }))}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Confirm Delete</DialogTitle>
                    </DialogHeader>
                    <div className="py-2">
                        {confirmDialog.type === 'main' && <p>Are you sure you want to delete this main category?</p>}
                        {confirmDialog.type === 'sub' && <p>Are you sure you want to delete this sub category?</p>}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setConfirmDialog({ open: false, type: null, id: null })}>
                            Cancel
                        </Button>
                        <Button onClick={performConfirmedDelete}>Delete</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
